import {useState} from 'react';
import type {RefObject} from 'react';
import {useResizeObserver} from '@sail/react-aria';
import {useThrottle} from '@sail/react';
import {useIsomorphicLayoutEffect} from '@sail/engine';
import {useMutationObserver} from '../components/hooks/useMutationObserver';

interface UseScrollOverflowReturn {
  isScrolled: boolean;
  isOverflowing: boolean;
  onScroll: () => void;
}

/**
 * Tracks whether a scroll container's content overflows and whether
 * the user has scrolled away from the top.
 */
export function useScrollOverflow(
  containerRef: RefObject<HTMLElement>,
  scrollRef: RefObject<HTMLElement>,
): UseScrollOverflowReturn {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const onScroll = useThrottle(() => {
    if (scrollRef.current) {
      // eslint-disable-next-line @sail/ui-no-reflow
      const {scrollTop, scrollHeight, offsetHeight, clientHeight} =
        scrollRef.current;

      setIsScrolled(Math.ceil(scrollTop) > 0);

      const hasOverflow = scrollHeight > clientHeight;
      const scrolledToBottom =
        Math.abs(scrollTop - (scrollHeight - offsetHeight)) < 1;

      setIsOverflowing(hasOverflow && !scrolledToBottom);
    }
  }, 16);

  // When content changes, overflow may change without firing scroll events
  useResizeObserver({ref: containerRef, onResize: onScroll});

  // When content changes, overflow may change without firing resize or scroll events
  useMutationObserver({node: scrollRef.current, handler: onScroll});

  useIsomorphicLayoutEffect(() => {
    onScroll();
  }, [onScroll]);

  return {isScrolled, isOverflowing, onScroll};
}
